/**
 * Embauche pipeline — Kanban view of every candidate in the onboarding /
 * contract / Academy funnel. Built to give RH a bird's-eye view of the
 * hiring process (previously scattered across each candidate profile).
 *
 * Data source: GET /api/hiring/pipeline
 * Drill-down: click a card → drawer with full details (form data, contracts,
 * academy engagement) + quick actions (view PDF, resend, etc.)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import { ExportPayfitDialog } from "../components/ExportPayfitDialog";
import { QuickOnboardingDialog } from "../components/QuickOnboardingDialog";
import { RenewContractDialog } from "../components/RenewContractDialog";
import BirthdayModal from "../components/BirthdayModal";
import DeliveryNoteModal from "../components/DeliveryNoteModal";
import RemoveFromOnboardingButton from "../components/candidates/RemoveFromOnboardingButton";
import CodeRdBadge from "../components/candidates/CodeRdBadge";
import InlineContractRoleDropdown from "../components/candidates/InlineContractRoleDropdown";
import EndTrialButton from "../components/candidates/EndTrialButton";
import {
    AlertTriangle,
    ArrowRight,
    Archive,
    Ban,
    Check,
    CheckCircle2,
    CheckSquare,
    Circle,
    ClipboardCheck,
    Download,
    Eye,
    FileSignature,
    FileText,
    Filter,
    Fingerprint,
    GraduationCap,
    GripVertical,
    Loader2,
    Mail,
    MailWarning,
    MousePointerClick,
    RefreshCw,
    Search,
    Send,
    ShieldAlert,
    X,
} from "lucide-react";
import { toast } from "sonner";

import ConfirmModal from "../components/common/ConfirmModal";
import { api, API_BASE, tokenStore } from "../lib/api";
import { useAuth } from "../lib/auth";
import { initials } from "../lib/format";

// ---------- stage config -----------------------------------------------------

const STAGE_ORDER = ["to_trigger", "form_sent", "form_received", "contract_ready", "signed"];
const STAGE_META = {
    to_trigger: {
        label: "À déclencher",
        hint: "Envoyer la fiche d'embauche",
        color: "border-slate-300 bg-slate-50",
        badge: "bg-slate-100 text-slate-700",
    },
    form_sent: {
        label: "Fiche envoyée",
        hint: "En attente du candidat",
        color: "border-amber-300 bg-amber-50/60",
        badge: "bg-amber-100 text-amber-800",
    },
    form_received: {
        label: "Fiche reçue",
        hint: "Préparer le contrat",
        color: "border-blue-300 bg-blue-50/60",
        badge: "bg-blue-100 text-blue-800",
    },
    contract_ready: {
        label: "Contrat en signature",
        hint: "En attente de signature",
        color: "border-purple-300 bg-purple-50/60",
        badge: "bg-purple-100 text-purple-800",
    },
    signed: {
        label: "Signé",
        hint: "Contrat signé + Academy",
        color: "border-emerald-400 bg-emerald-50/60",
        badge: "bg-emerald-100 text-emerald-800",
    },
};

// ---------- utilities --------------------------------------------------------

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
}

function fmtDay(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    } catch { return "—"; }
}

// ---------- main page --------------------------------------------------------

export default function Embauche() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    // iter84 · Toggle PayFit onboardé — admins ou pré-recruteurs
    // habilités via /utilisateurs (can_access_embauche=true).
    const canTogglePayfit = isAdmin || !!user?.can_access_embauche;
    // iter110m-hotfix3 · Actions Coordinateur RH — gate frontend selon les
    // permissions cochées dans /utilisateurs. Le backend re-guard toujours,
    // ce sont juste des UX prophylactiques pour ne pas montrer un bouton
    // qui renverra 403.
    const canSendOnboarding = isAdmin || !!user?.can_send_onboarding;
    const canRemoveFromPipeline = isAdmin || !!user?.can_remove_from_pipeline;
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [drawer, setDrawer] = useState(null);
    // Bulk-selection mode (Set of candidate_ids) — activated via the checkbox
    // in the top bar. When non-empty, a floating action bar appears at the
    // bottom to trigger group actions (e.g. envoyer les fiches en lot).
    const [selectionMode, setSelectionMode] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [confirmSetAside, setConfirmSetAside] = useState(false);
    // Manual drag-and-drop override state (admin only). We stash the pending
    // move in local state and open a confirmation modal — the API call only
    // fires once the admin confirms + types a reason (audit trail).
    const [activeDrag, setActiveDrag] = useState(null); // { item, from_stage }
    const [pendingMove, setPendingMove] = useState(null); // { item, from_stage, to_stage }
    // iter110n · CEO 2026-09-27 · Modal d'envoi manuel du mail anniversaire
    // (clic sur le 🎂 sur une carte Kanban). Voir components/BirthdayModal.
    const [birthdayModalCandidate, setBirthdayModalCandidate] = useState(null);
    // Drag is enabled only when we're not in bulk-select mode and the user
    // has admin role. Referents / pre-recruteurs see the Kanban but cannot
    // force-move candidates.
    const dragEnabled = isAdmin && !selectionMode;

    // Pointer sensor with 6px activation distance — small movements are
    // interpreted as clicks (still opens the drawer), only actual drags
    // trigger DnD.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const handleDragStart = useCallback((event) => {
        const cid = event?.active?.id;
        if (!cid || !pipeline?.items) return;
        const item = pipeline.items.find((x) => x.candidate_id === cid);
        if (item) setActiveDrag({ item, from_stage: item.stage });
    }, [pipeline]);

    const handleDragEnd = useCallback((event) => {
        const activeCid = event?.active?.id;
        const overId = event?.over?.id;
        setActiveDrag(null);
        if (!activeCid || !overId) return;
        // Column droppable IDs are prefixed `stage:<stage_id>` to avoid
        // colliding with candidate IDs (both are strings otherwise).
        if (typeof overId !== "string" || !overId.startsWith("stage:")) return;
        const toStage = overId.slice("stage:".length);
        const item = pipeline?.items?.find((x) => x.candidate_id === activeCid);
        if (!item) return;
        if (item.stage === toStage) return; // no-op — same column
        setPendingMove({ item, from_stage: item.stage, to_stage: toStage });
    }, [pipeline]);

    const handleDragCancel = useCallback(() => {
        setActiveDrag(null);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/hiring/pipeline", {
                params: search ? { search } : {},
            });
            setPipeline(data);
        } catch (e) {
            toast.error("Impossible de charger le pipeline embauche");
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        const t = setTimeout(load, search ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, search]);

    const toggleSelected = useCallback((cid) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(cid)) next.delete(cid);
            else next.add(cid);
            return next;
        });
    }, []);

    const selectMany = useCallback((cids) => {
        setSelected(prev => {
            const next = new Set(prev);
            cids.forEach(id => next.add(id));
            return next;
        });
    }, []);

    const deselectMany = useCallback((cids) => {
        setSelected(prev => {
            const next = new Set(prev);
            cids.forEach(id => next.delete(id));
            return next;
        });
    }, []);

    // iter84 · Toggle PayFit onboardé — optimistic update sur la carte
    // du candidat + rollback + toast en cas d'erreur.
    const togglePayfit = useCallback(async (candidateId, currentlyDone) => {
        const nextDone = !currentlyDone;
        // Optimistic update sur pipeline.items ET pipeline.by_stage.signed
        setPipeline((prev) => {
            if (!prev) return prev;
            const patch = (it) => it.candidate_id === candidateId
                ? {
                    ...it,
                    payfit_onboarded_at: nextDone ? new Date().toISOString() : null,
                    payfit_onboarded_by_name: nextDone ? (user?.name || user?.email) : null,
                }
                : it;
            return {
                ...prev,
                items: (prev.items || []).map(patch),
                by_stage: Object.fromEntries(
                    Object.entries(prev.by_stage || {}).map(([k, list]) => [k, (list || []).map(patch)]),
                ),
            };
        });
        try {
            await api.post(`/candidates/${candidateId}/mark-payfit-onboarded`, { done: nextDone });
            toast.success(nextDone
                ? "Marqué comme onboardé sur PayFit ✅"
                : "Tampon PayFit retiré");
        } catch (e) {
            // Rollback : recharge complet du pipeline pour rester cohérent
            toast.error(e.response?.data?.detail || "Échec de la mise à jour PayFit");
            load();
        }
    }, [user, load]);

    // iter110n · CEO 2026-09-22 · Attribution poste inline sur la carte.
    // Optimistic update — patch pipeline.items + pipeline.by_stage.<stage>
    // avec le nouveau slug + label résolu par le composant. Pas d'appel
    // API ici : le composant `InlineContractRoleDropdown` a déjà tapé
    // `POST /candidates/{id}/contract-role` et affiché son toast avant
    // d'appeler cette callback.
    const updateContractRole = useCallback((candidateId, newSlug, newLabel) => {
        setPipeline((prev) => {
            if (!prev) return prev;
            const patch = (it) => it.candidate_id === candidateId
                ? { ...it, contract_role: newSlug, contract_role_label: newLabel || null }
                : it;
            return {
                ...prev,
                items: (prev.items || []).map(patch),
                by_stage: Object.fromEntries(
                    Object.entries(prev.by_stage || {}).map(([k, list]) => [k, (list || []).map(patch)]),
                ),
            };
        });
    }, []);

    // iter101 · Rattrapage code_rd depuis la carte (colonne "Signé").
    // Admin uniquement. Appelle `/api/code-rd/candidates/{id}/assign` qui
    // matche l'historique OU génère un code AT frais. Idempotent — un
    // candidat qui a déjà un code se voit renvoyer son code existant.
    //
    // iter110n · CEO 2026-09-22 — 1er clic = force_source="auto" (prend
    // le prochain code auto directement, sans passer par l'arbitrage
    // fuzzy). Le CEO déclare implicitement en cliquant que ce recruteur
    // est nouveau (ex. Flament Quentin). Si l'admin veut arbitrer un
    // match historique, il passe par /admin/codes-rd. Cette route
    // 1-clic est optimisée pour le cas majoritaire.
    const assignCodeRd = useCallback(async (candidateId) => {
        try {
            const { data } = await api.post(
                `/admin/code-rd/candidates/${candidateId}/assign`,
                { force_source: "auto" },
            );
            const code = data?.code;
            const source = data?.source;
            if (code) {
                if (source === "already") {
                    toast.info(`Code déjà attribué : ${code}`);
                } else {
                    toast.success(`Code attribué : ${code}`);
                }
            } else {
                // Ne devrait plus arriver avec force_source="auto", mais on
                // conserve le fallback pour les cas edge (candidat is_test etc.)
                toast.warning(data?.message || "Attribution impossible — voir /code-rd");
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible d'attribuer un code");
        }
    }, [load]);

    const clearSelection = useCallback(() => {
        setSelected(new Set());
        setSelectionMode(false);
    }, []);

    const sendFiche = useCallback(async (candidateId) => {
        try {
            await api.post(`/onboarding/candidates/${candidateId}/send`);
            toast.success("Fiche d'embauche envoyée");
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Impossible d'envoyer la fiche";
            toast.error(msg);
        }
    }, [load]);

    const bulkSendFiches = useCallback(async () => {
        if (selected.size === 0) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/hiring/bulk/send-fiches", {
                candidate_ids: Array.from(selected),
            });
            const sentCount = data.sent?.length || 0;
            const failedCount = data.failed?.length || 0;
            if (failedCount === 0) {
                toast.success(`${sentCount} fiche${sentCount > 1 ? "s" : ""} envoyée${sentCount > 1 ? "s" : ""}`);
            } else {
                toast.warning(`${sentCount} envoyée(s), ${failedCount} échec(s)`);
            }
            clearSelection();
            load();
        } catch (e) {
            toast.error("L'envoi en lot a échoué");
        } finally {
            setBulkBusy(false);
        }
    }, [selected, clearSelection, load]);

    const bulkSetAside = useCallback(async () => {
        if (selected.size === 0) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/hiring/bulk/set-aside", {
                candidate_ids: Array.from(selected),
            });
            const doneCount = data.set_aside?.length || 0;
            const skippedCount = data.skipped?.length || 0;
            const failedCount = data.failed?.length || 0;
            if (failedCount === 0 && doneCount > 0) {
                toast.success(
                    `${doneCount} candidat${doneCount > 1 ? "s" : ""} retiré${doneCount > 1 ? "s" : ""} du pipeline`
                    + (skippedCount ? ` · ${skippedCount} déjà écarté(s)` : ""),
                );
            } else if (failedCount === 0 && doneCount === 0) {
                toast.info(`Aucun changement · ${skippedCount} déjà écarté(s)`);
            } else {
                toast.warning(`${doneCount} retiré(s), ${failedCount} échec(s)`);
            }
            setConfirmSetAside(false);
            clearSelection();
            load();
        } catch (e) {
            toast.error("Le retrait en lot a échoué");
        } finally {
            setBulkBusy(false);
        }
    }, [selected, clearSelection, load]);

    const totalCount = pipeline?.items?.length || 0;
    const counts = pipeline?.counts || {};

    return (
        <div className="min-h-full bg-shariti-cream" data-testid="embauche-page">
            {/* Sticky header */}
            <div className="sticky top-0 z-20 bg-shariti-cream/95 backdrop-blur-sm border-b border-shariti-border">
                <div className="px-6 py-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2">
                                <ClipboardCheck className="w-5 h-5 text-shariti-orange" />
                                <h1 className="text-2xl font-black tracking-tight text-shariti-black">
                                    Embauche & Contrats
                                </h1>
                            </div>
                            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                                Pilote de bout en bout la fiche d&apos;embauche → contrat signé → Academy.
                                Tout le process, dans une seule vue.
                            </p>
                            {dragEnabled && (
                                <p
                                    className="text-[11px] text-slate-400 mt-1.5 inline-flex items-center gap-1"
                                    data-testid="embauche-drag-hint"
                                >
                                    <GripVertical className="w-3 h-3" />
                                    Glisser une carte vers une autre colonne pour un déplacement manuel (admin override).
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un candidat…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    data-testid="embauche-search"
                                    className="pl-9 pr-3 py-2 rounded-full border border-shariti-border bg-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange transition"
                                />
                            </div>
                            <QuickOnboardingDialog onCreated={load} />
                            <ExportPayfitDialog />
                            <a
                                href="/embauche/audit"
                                data-testid="embauche-audit-link"
                                title="Diagnostiquer un candidat manquant dans le pipeline"
                                className="px-3 py-2 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-black uppercase tracking-widest hover:bg-emerald-100 transition inline-flex items-center gap-1.5"
                            >
                                <Search className="w-3.5 h-3.5" />
                                Audit
                            </a>
                            <button
                                type="button"
                                onClick={() => { setSelectionMode(s => !s); if (selectionMode) setSelected(new Set()); }}
                                data-testid="embauche-toggle-selection"
                                title={selectionMode ? "Quitter la sélection groupée" : "Activer la sélection groupée"}
                                className={`px-3 py-2 rounded-full border text-xs font-black uppercase tracking-widest transition ${selectionMode ? "bg-shariti-orange text-white border-shariti-orange" : "bg-white text-slate-600 border-shariti-border hover:border-shariti-orange hover:text-shariti-orange"}`}
                            >
                                <CheckSquare className="w-4 h-4 inline-block -mt-0.5 mr-1" />
                                Sélection
                            </button>
                            <button
                                type="button"
                                onClick={load}
                                data-testid="embauche-refresh"
                                title="Rafraîchir"
                                className="p-2 rounded-full border border-shariti-border bg-white hover:bg-shariti-orange-soft/30 transition"
                            >
                                <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
                            </button>
                        </div>
                    </div>

                    {/* Stage counters */}
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                        {STAGE_ORDER.map((s) => (
                            <div
                                key={s}
                                data-testid={`embauche-stage-count-${s}`}
                                className={`rounded-xl px-3 py-2 border ${STAGE_META[s].color}`}
                                aria-label={`${STAGE_META[s].label}: ${counts[s] ?? 0}`}
                            >
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                    {STAGE_META[s].label}
                                </div>
                                <div className="flex items-baseline justify-between mt-0.5">
                                    <div className="text-2xl font-black text-shariti-black leading-none">
                                        {counts[s] ?? 0}
                                    </div>
                                    <div className="text-[10px] text-slate-400">{STAGE_META[s].hint}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Kanban */}
            <div className="px-4 pb-8 pt-4">
                {loading && !pipeline ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader2 className="w-6 h-6 animate-spin text-shariti-orange" />
                    </div>
                ) : totalCount === 0 ? (
                    <EmptyPipeline />
                ) : (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                            {STAGE_ORDER.map((stage) => (
                                <StageColumn
                                    key={stage}
                                    stage={stage}
                                    items={pipeline?.by_stage?.[stage] || []}
                                    onCardClick={setDrawer}
                                    selectionMode={selectionMode}
                                    selected={selected}
                                    onToggleSelect={toggleSelected}
                                    onSelectAll={selectMany}
                                    onDeselectAll={deselectMany}
                                    onQuickSendFiche={sendFiche}
                                    onCardRemoved={load}
                                    dragEnabled={dragEnabled}
                                    isDragTarget={activeDrag && activeDrag.from_stage !== stage}
                                    canTogglePayfit={canTogglePayfit}
                                    onTogglePayfit={togglePayfit}
                                    canAssignCode={isAdmin}
                                    onAssignCode={assignCodeRd}
                                    onRoleChanged={updateContractRole}
                                    onBirthdayClick={setBirthdayModalCandidate}
                                />
                            ))}
                        </div>
                        <DragOverlay dropAnimation={null}>
                            {activeDrag ? (
                                <div
                                    className="w-64 bg-white rounded-xl border-2 border-shariti-orange shadow-2xl p-3 rotate-2 pointer-events-none"
                                    data-testid="embauche-drag-overlay"
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center text-white font-black text-xs">
                                            {initials(`${activeDrag.item.first_name} ${activeDrag.item.last_name}`)}
                                        </div>
                                        <div className="font-bold text-sm text-shariti-black break-words min-w-0">
                                            {`${activeDrag.item.first_name} ${activeDrag.item.last_name}`.trim() || activeDrag.item.email}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                )}
            </div>

            {/* Floating bulk action bar */}
            {selectionMode && selected.size > 0 && (
                <div
                    data-testid="embauche-bulk-bar"
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-shariti-black text-white shadow-2xl rounded-full pl-5 pr-2 py-2 flex items-center gap-3 border-2 border-shariti-orange"
                >
                    <span className="text-sm font-black">
                        {selected.size} candidat{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
                    </span>
                    {canSendOnboarding && (
                    <button
                        type="button"
                        onClick={bulkSendFiches}
                        disabled={bulkBusy}
                        data-testid="embauche-bulk-send-fiches"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-shariti-orange hover:bg-[#d66528] text-white text-xs font-black uppercase tracking-widest transition disabled:opacity-60"
                    >
                        {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Envoyer les fiches
                    </button>
                    )}
                    {canRemoveFromPipeline && (
                    <button
                        type="button"
                        onClick={() => setConfirmSetAside(true)}
                        disabled={bulkBusy}
                        data-testid="embauche-bulk-set-aside"
                        title="Retirer les candidats sélectionnés du pipeline (les archive dans Écartés)"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest transition disabled:opacity-60"
                    >
                        <Archive className="w-3.5 h-3.5" />
                        Retirer du pipeline
                    </button>
                    )}
                    <button
                        type="button"
                        onClick={clearSelection}
                        data-testid="embauche-bulk-cancel"
                        className="p-1.5 rounded-full hover:bg-white/10 transition"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Drawer */}
            {drawer && (
                <CandidateDrawer
                    item={drawer}
                    onClose={() => setDrawer(null)}
                    onRefresh={load}
                />
            )}

            {/* iter110n · CEO 2026-09-27 · Modal d'envoi manuel du mail
                anniversaire (clic sur le 🎂 sur une carte Kanban). */}
            <BirthdayModal
                open={!!birthdayModalCandidate}
                onOpenChange={(v) => !v && setBirthdayModalCandidate(null)}
                candidateId={birthdayModalCandidate?.candidate_id}
                candidateName={birthdayModalCandidate?.name}
                onSent={() => {
                    setBirthdayModalCandidate(null);
                    load();
                }}
            />

            {/* Confirm bulk set-aside */}
            {confirmSetAside && (
                <ConfirmModal
                    testid="embauche-bulk-set-aside-modal"
                    tone="red"
                    icon={AlertTriangle}
                    title={`Retirer ${selected.size} candidat${selected.size > 1 ? "s" : ""} du pipeline ?`}
                    body={
                        <div className="space-y-2">
                            <p>
                                Les candidats sélectionnés seront <strong>archivés dans « Écartés »</strong>.
                                Ils disparaissent du pipeline Embauche, Pépites, Alertes et À appeler,
                                mais restent consultables et récupérables à tout moment depuis l&apos;onglet
                                <em> Écartés</em>.
                            </p>
                            <p className="text-xs text-slate-500">
                                Cette action est <strong>réversible</strong> — utile pour nettoyer les anciens
                                candidats qui n&apos;ont jamais donné suite.
                            </p>
                        </div>
                    }
                    confirmLabel={bulkBusy ? "Retrait…" : "Retirer du pipeline"}
                    cancelLabel="Annuler"
                    onConfirm={bulkBusy ? () => {} : bulkSetAside}
                    onCancel={bulkBusy ? () => {} : () => setConfirmSetAside(false)}
                />
            )}

            {/* Confirm manual drag-and-drop stage override (admin only) */}
            {pendingMove && (
                <ForceStageModal
                    move={pendingMove}
                    onClose={() => setPendingMove(null)}
                    onConfirmed={() => { setPendingMove(null); load(); }}
                />
            )}
        </div>
    );
}

// ---------- Column ----------------------------------------------------------

function StageColumn({ stage, items, onCardClick, selectionMode, selected, onToggleSelect, onQuickSendFiche, onSelectAll, onDeselectAll, onCardRemoved, dragEnabled, isDragTarget, canTogglePayfit, onTogglePayfit, canAssignCode, onAssignCode, onRoleChanged, onBirthdayClick }) {
    const meta = STAGE_META[stage];
    // Number of items in this column that are already selected — used to
    // decide whether the "select all in column" button toggles ON or OFF.
    const selectedInCol = selectionMode && selected
        ? items.filter((it) => selected.has(it.candidate_id)).length
        : 0;
    const allSelected = items.length > 0 && selectedInCol === items.length;

    // iter84 · Compteur "X / Y onboardés PayFit" — uniquement sur la
    // colonne "Signé", où le toggle est disponible.
    const payfitDoneCount = stage === "signed"
        ? items.filter((it) => !!it.payfit_onboarded_at).length
        : 0;

    // Droppable — declared even when drag is disabled so the ref remains
    // stable across role switches; `disabled` short-circuits the drop logic.
    const { isOver, setNodeRef } = useDroppable({
        id: `stage:${stage}`,
        disabled: !dragEnabled,
    });

    // Visual affordance : when the admin is currently dragging a card, all
    // OTHER columns get a subtle orange dashed outline hinting "drop here".
    // The column being hovered receives a stronger highlight.
    const dropClass = !dragEnabled
        ? ""
        : isOver
            ? "ring-2 ring-shariti-orange ring-offset-2 ring-offset-shariti-cream"
            : isDragTarget
                ? "outline-dashed outline-2 outline-shariti-orange/60 outline-offset-[-4px]"
                : "";

    return (
        <div
            ref={setNodeRef}
            data-testid={`embauche-column-${stage}`}
            data-drop-target={dragEnabled ? "true" : undefined}
            className={`rounded-2xl border-2 ${meta.color} p-3 min-h-[400px] transition-all ${dropClass}`}
        >
            <div className="flex items-center justify-between mb-3 px-1 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-wider text-shariti-black truncate">
                        {meta.label}
                    </div>
                    <div className={`px-1.5 py-0.5 rounded-full text-[10px] font-black shrink-0 ${meta.badge}`}>
                        {items.length}
                    </div>
                    {stage === "signed" && items.length > 0 && (
                        <div
                            data-testid={`embauche-payfit-counter-${stage}`}
                            title={`${payfitDoneCount} sur ${items.length} onboardé${payfitDoneCount > 1 ? "s" : ""} sur PayFit`}
                            className={`px-1.5 py-0.5 rounded-full text-[10px] font-black shrink-0 inline-flex items-center gap-1 ${payfitDoneCount === items.length
                                ? "bg-emerald-200 text-emerald-900"
                                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}
                        >
                            <Check className="w-2.5 h-2.5" strokeWidth={3} />
                            {payfitDoneCount}/{items.length} PayFit
                        </div>
                    )}
                </div>
                {selectionMode && items.length > 0 && (
                    <button
                        type="button"
                        onClick={() => allSelected
                            ? onDeselectAll?.(items.map((it) => it.candidate_id))
                            : onSelectAll?.(items.map((it) => it.candidate_id))}
                        data-testid={`embauche-column-select-all-${stage}`}
                        title={allSelected
                            ? `Désélectionner les ${items.length} candidat${items.length > 1 ? "s" : ""} de cette colonne`
                            : `Sélectionner les ${items.length} candidat${items.length > 1 ? "s" : ""} de cette colonne`
                        }
                        aria-label={allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                        className={`shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md border transition ${
                            allSelected
                                ? "bg-shariti-orange border-shariti-orange text-white hover:bg-[#d66528]"
                                : "bg-white border-shariti-gray text-slate-400 hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        {allSelected
                            ? <CheckSquare className="w-3.5 h-3.5" strokeWidth={2.6} />
                            : <Circle className="w-3.5 h-3.5" strokeWidth={2.2} />}
                    </button>
                )}
            </div>
            <div className="space-y-2">
                {items.length === 0 ? (
                    <div className="text-center py-8 text-xs text-slate-400 italic">
                        Vide
                    </div>
                ) : (
                    items.map((it) => (
                        <PipelineCard
                            key={it.candidate_id}
                            item={it}
                            onClick={() => onCardClick(it)}
                            selectionMode={selectionMode}
                            isSelected={selected?.has(it.candidate_id)}
                            onToggleSelect={onToggleSelect}
                            onQuickSendFiche={onQuickSendFiche}
                            onRemoved={onCardRemoved}
                            dragEnabled={dragEnabled}
                            canTogglePayfit={canTogglePayfit}
                            onTogglePayfit={onTogglePayfit}
                            canAssignCode={canAssignCode}
                            onAssignCode={onAssignCode}
                            onRoleChanged={onRoleChanged}
                            onBirthdayClick={onBirthdayClick}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

// ---------- Card ------------------------------------------------------------

function PipelineCard({ item, onClick, selectionMode, isSelected, onToggleSelect, onQuickSendFiche, onRemoved, dragEnabled, canTogglePayfit, onTogglePayfit, canAssignCode, onAssignCode, onRoleChanged, onBirthdayClick }) {
    const name = `${item.first_name} ${item.last_name}`.trim() || item.email || "—";
    const academy = item.academy || {};
    const stage = item.stage;

    // iter84 · Tampon PayFit onboardé — visible uniquement en colonne
    // "Signé" (stage === "signed"). Teinte la carte en vert menthe.
    const payfitDone = !!item.payfit_onboarded_at;
    const showPayfitToggle = stage === "signed" && !!canTogglePayfit && !selectionMode;

    // "activity date" = most recent event on this card
    const activityDate = (
        item.contract?.signed_at || item.contract?.sent_at || item.contract?.created_at ||
        item.onboarding?.submitted_at || item.onboarding?.updated_at || item.onboarding?.sent_at
    );

    // Draggable — attached to a dedicated grip handle so clicking anywhere
    // else on the card still opens the drawer (activationConstraint on the
    // sensor also protects against accidental drags on small pointer moves).
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: item.candidate_id,
        disabled: !dragEnabled,
    });

    const handleClick = (e) => {
        if (isDragging) return; // ignore stray click at end of drag
        if (selectionMode) {
            e.preventDefault();
            onToggleSelect?.(item.candidate_id);
        } else {
            onClick?.();
        }
    };

    return (
        <div
            ref={setNodeRef}
            data-testid={`embauche-card-${item.candidate_id}`}
            data-drag-enabled={dragEnabled ? "true" : "false"}
            data-payfit-onboarded={payfitDone ? "true" : "false"}
            className={`relative w-full rounded-xl border transition p-3 group cursor-pointer ${isDragging ? "opacity-30" : ""} ${isSelected
                ? "border-shariti-orange ring-2 ring-shariti-orange/20 shadow-md bg-white"
                : payfitDone
                    ? "border-emerald-300 bg-emerald-50/40 hover:border-emerald-500 hover:shadow-md"
                    : "border-shariti-border bg-white hover:border-shariti-orange hover:shadow-md"
                }`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(e); } }}
        >
            {/* Drag grip — admin only.
                • Absolute-positioned so it does NOT consume flex width.
                • Hidden by default (opacity-0), fades in on card hover.
                  This keeps the resting layout identical to pre-drag era
                  (columns can shrink to ~130px on wide-screens-with-sidebar
                  without ever squeezing the name text).
                • Listeners are attached ONLY to this button → clicking the
                  rest of the card still opens the drawer. */}
            {dragEnabled && (
                <button
                    type="button"
                    aria-label={`Glisser ${name} vers une autre colonne`}
                    title="Glisser vers une autre colonne (override manuel admin)"
                    data-testid={`embauche-card-drag-${item.candidate_id}`}
                    onClick={(e) => e.stopPropagation()}
                    {...listeners}
                    {...attributes}
                    className="absolute -top-1 -left-1 z-10 p-1 rounded-full bg-white border border-shariti-border shadow-sm text-slate-400 hover:text-shariti-orange hover:border-shariti-orange opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition cursor-grab active:cursor-grabbing touch-none"
                >
                    <GripVertical className="w-3 h-3" strokeWidth={2.6} />
                </button>
            )}
            <div className="flex items-start gap-2.5">
                {selectionMode && (
                    <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => onToggleSelect?.(item.candidate_id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`embauche-card-checkbox-${item.candidate_id}`}
                        className="mt-1.5 w-4 h-4 accent-shariti-orange flex-shrink-0"
                    />
                )}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center text-white font-black text-xs flex-shrink-0">
                    {initials(`${item.first_name} ${item.last_name}`)}
                </div>
                <div className="min-w-0 flex-1">
                    {/* Name typography tuned for narrow columns:
                        • `text-sm` (back to original size — cards read cleanly).
                        • `break-words` allows a last-resort char-break for
                          extremely long single words (rare — most names
                          wrap at whitespace first).
                        • `title` attribute exposes the full name on hover
                          for any residual truncation.
                        • **CRITICAL**: the name div is now a direct child
                          of the content wrapper — the ex-inner flex row
                          around it was reserving 30px (24px button + 6px
                          gap) even when the Remove button was opacity-0,
                          which squeezed the name into ~30px on narrow
                          production columns and forced `break-words` to
                          shred words like "RABUSSEAU" into 4 lines. The
                          Remove button is now absolute-positioned on the
                          card (see below). */}
                    <div className="flex items-center gap-1.5 min-w-0">
                        <div
                            className="font-bold text-sm leading-snug text-shariti-black break-words group-hover:text-shariti-orange transition min-w-0 flex-1"
                            title={name}
                        >
                            {name}
                        </div>
                        {showPayfitToggle && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTogglePayfit?.(item.candidate_id, payfitDone);
                                }}
                                data-testid={`embauche-card-payfit-toggle-${item.candidate_id}`}
                                aria-pressed={payfitDone}
                                title={payfitDone
                                    ? `Onboardé PayFit${item.payfit_onboarded_by_name ? ` par ${item.payfit_onboarded_by_name}` : ""} — cliquer pour retirer le tampon`
                                    : "Marquer comme onboardé sur PayFit"
                                }
                                className={`shrink-0 h-4 w-4 inline-flex items-center justify-center rounded-full border transition ${payfitDone
                                    ? "bg-emerald-500 border-emerald-600 text-white hover:bg-emerald-600"
                                    : "bg-white border-slate-300 text-transparent hover:border-emerald-400 hover:text-emerald-400"
                                    }`}
                            >
                                <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                            </button>
                        )}
                        {/* iter110n · CEO 2026-09-25 · Badge 🎂 anniversaire.
                            Affiché uniquement pour les collaborateurs en contrat
                            signé actif (le backend filtre déjà via
                            find_birthday_recipients ; côté carte on affiche dès
                            que birthday_today=true — cartes signed/embauché).
                            CEO 2026-09-27 · WhatsApp : clic → ouvre le modal
                            d'envoi manuel (BirthdayModal). Badge grisé (avec
                            check ✓) si déjà envoyé aujourd'hui. */}
                        {item.birthday_today && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onBirthdayClick?.({
                                        candidate_id: item.candidate_id,
                                        name: `${item.first_name} ${item.last_name}`.trim(),
                                    });
                                }}
                                data-testid={`embauche-card-birthday-${item.candidate_id}`}
                                title={
                                    item.birthday_greeting_sent_today
                                        ? `🎂 ✓ Mail d'anniversaire déjà envoyé aujourd'hui à ${item.first_name}. Clique pour renvoyer.`
                                        : `🎂 Anniversaire aujourd'hui — clique pour envoyer un mail à ${item.first_name}.`
                                }
                                className={`shrink-0 inline-flex items-center justify-center text-[13px] leading-none rounded-full transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 ${
                                    item.birthday_greeting_sent_today
                                        ? "opacity-50 saturate-50"
                                        : "animate-bounce"
                                }`}
                                style={item.birthday_greeting_sent_today ? {} : { animationDuration: "2.4s" }}
                            >
                                {item.birthday_greeting_sent_today ? (
                                    <span className="relative">
                                        🎂
                                        <span className="absolute -bottom-0.5 -right-1 bg-emerald-500 text-white rounded-full w-2.5 h-2.5 flex items-center justify-center text-[7px] font-bold leading-none">
                                            ✓
                                        </span>
                                    </span>
                                ) : (
                                    "🎂"
                                )}
                            </button>
                        )}
                    </div>
                    {/* iter110n · Dropdown inline pour attribuer/modifier le rôle
                        contrat (Recruteur / Recruteur confirmé / Responsable…).
                        Le libellé affiché = contract_role_label si posé, sinon
                        `item.position` (fallback historique). Le choix est
                        persisté sur le candidat et pré-remplit le formulaire de
                        contrat sur la fiche complète. */}
                    <InlineContractRoleDropdown
                        candidateId={item.candidate_id}
                        contractRoleSlug={item.contract_role}
                        contractRoleLabel={item.contract_role_label}
                        fallbackLabel={item.position}
                        onChange={({ slug, label }) => onRoleChanged?.(item.candidate_id, slug, label)}
                        dataTestidPrefix="embauche-card-role"
                    />
                </div>
            </div>

            {/* Escape hatch — retirer le candidat du circuit sans passer
                par sa fiche. Absolute-positioned top-LEFT corner (iter101) so
                it does NOT overlap the PayFit toggle (which sits top-right of
                the header). Visible only on card hover. Selection mode
                reprend la main dessus. */}
            {!selectionMode && (
                <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                    <RemoveFromOnboardingButton
                        candidate={{
                            id: item.candidate_id,
                            // Kanban cards are BY DEFINITION in the funnel
                            // (they wouldn't render otherwise), so we skip
                            // the status check.
                            status: "onboarding",
                            onboarding_form_id: item.onboarding?.id || null,
                        }}
                        onDone={() => onRemoved?.(item.candidate_id)}
                        variant="icon"
                        skipStatusCheck
                        dataTestid={`embauche-card-remove-${item.candidate_id}`}
                    />
                </div>
            )}

            {/* stage-specific detail line */}
            <div className="mt-2.5 text-[11px] text-slate-500 flex items-center justify-between">
                <StageDetail stage={stage} item={item} />
                <span className="text-[10px] text-slate-400">{fmtDay(activityDate)}</span>
            </div>

            {/* Unread-email alert — the CEO wants to spot candidates who
                aren't reading their emails at a glance instead of clicking
                into each profile. Backend flags `has_unread_alert=true`
                when ≥1 email was sent AND NONE opened AND ≥24h have
                elapsed since the last send. */}
            {item.emails_summary?.has_unread_alert && (
                <div
                    data-testid={`embauche-card-unread-alert-${item.candidate_id}`}
                    title={`Aucun email lu depuis ${Math.round(item.emails_summary.unread_hours)}h — ${item.emails_summary.sent_count} envoyé(s)`}
                    className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                >
                    <MailWarning className="w-2.5 h-2.5" />
                    Non lu · {Math.round(item.emails_summary.unread_hours)}h
                </div>
            )}

            {/* Code RD (matricule) — universal join key.
                Shown on EVERY card as soon as a code is attached
                (per CEO brief 2026-07-29 point 3, iter57). Click to
                copy the code to clipboard for pasting into Evergiving.
                The "Sans code" dashed pill is only shown on `signed`
                stage below because it's an actionable rattrapage
                signal (a lead in "propose_sent" without a code is
                normal — pre-hired). */}
            {item.code_rd && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <CodeRdBadge
                        code={item.code_rd}
                        candidateId={item.candidate_id}
                    />
                </div>
            )}

            {/* Academy engagement badges (only for signed) */}
            {stage === "signed" && (
                <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {/* "Sans code" cue for hired-without-matricule cases.
                        The positive code badge is rendered above regardless
                        of stage; this block only remains for the missing
                        case where an admin needs to act (Rattrapage). */}
                    {!item.code_rd && (
                        <>
                            <span
                                data-testid={`embauche-card-code-rd-missing-${item.candidate_id}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md font-mono text-[10px] font-black tracking-wider bg-slate-100 text-slate-400 border border-dashed border-slate-300 whitespace-nowrap"
                                title="Matricule non attribué — cliquez pour ouvrir la fiche"
                            >
                                <Fingerprint className="w-2.5 h-2.5" />
                                Sans code
                            </span>
                            {canAssignCode && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAssignCode?.(item.candidate_id);
                                    }}
                                    data-testid={`embauche-card-assign-code-${item.candidate_id}`}
                                    title="Attribuer un code RD à ce candidat (historique ou nouveau)"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-shariti-orange text-white text-[10px] font-black uppercase tracking-wider hover:bg-[#d66528] transition whitespace-nowrap"
                                >
                                    <Fingerprint className="w-2.5 h-2.5" />
                                    Attribuer
                                </button>
                            )}
                        </>
                    )}
                    {academy.clicked_at ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-wider whitespace-nowrap">
                            <MousePointerClick className="w-2.5 h-2.5" /> Academy cliqué
                        </span>
                    ) : academy.reminder_sent_at ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider whitespace-nowrap">
                            <Mail className="w-2.5 h-2.5" /> Relancé
                        </span>
                    ) : academy.invitation_sent_at ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider whitespace-nowrap">
                            <GraduationCap className="w-2.5 h-2.5" /> Academy envoyé
                        </span>
                    ) : null}
                </div>
            )}

            {/* iter110n · CEO 2026-09-23 · Bouton « Rompre l'essai » ou
                badge d'état « 🚫 Rompu le … » selon si un event
                trial_period_terminated existe déjà pour ce candidat.
                Option C (CEO 2026-09-24) : distinction claire action vs
                état — résout la confusion Tom Angeli / ops manager. */}
            {stage === "signed" && (
                <div
                    className="mt-2.5"
                    onClick={(e) => e.stopPropagation()}
                >
                    {item.trial_terminated_at ? (
                        <TrialEndedBadge item={item} />
                    ) : (
                        <EndTrialButton
                            candidate={{ id: item.candidate_id, first_name: item.first_name, last_name: item.last_name }}
                            compact
                        />
                    )}
                </div>
            )}

            {/* Quick action: send onboarding fiche for `to_trigger` stage */}
            {stage === "to_trigger" && !selectionMode && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onQuickSendFiche?.(item.candidate_id); }}
                    data-testid={`embauche-card-quick-send-${item.candidate_id}`}
                    className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] text-[11px] font-black uppercase tracking-wider transition"
                >
                    <Send className="w-3 h-3" />
                    Envoyer la fiche
                </button>
            )}

            {/* Quick action: RESEND the fiche when candidate is dragging their
                feet. Only shown on the `form_sent` column (fiche envoyée
                mais non complétée). Same endpoint as the first-send —
                `send_or_resend_invite` is idempotent and re-fresh the
                magic-link. Visual style intentionally different from the
                first-send button (outlined + refresh icon) so the CEO
                distinguishes "premier envoi" vs "relance". */}
            {stage === "form_sent" && !selectionMode && (
                <ResendFicheAction
                    candidateId={item.candidate_id}
                    sentAt={item.onboarding?.sent_at}
                    openedAt={item.onboarding?.opened_at}
                    reminderSentAt={item.onboarding?.reminder_sent_at}
                    onDone={onQuickSendFiche}
                />
            )}
        </div>
    );
}


/**
 * `ResendFicheAction` — small outlined button that resends the onboarding
 * fiche invite. Delegates to the parent's `onQuickSendFiche` handler
 * (same endpoint as the first-send, POST /onboarding/candidates/{id}/send,
 * which is idempotent). Displays a subtle "envoyée il y a X jours" hint
 * so the admin sees at a glance how long the candidate has been sitting.
 */

/**
 * iter110n · CEO 2026-09-24 · Badge d'état « Rompu le … par … » affiché
 * sur les cartes de la colonne "Signé" QUAND une rupture de période
 * d'essai a déjà été journalisée (event `trial_period_terminated`).
 *
 * Résout la confusion signalée par la chargée des ops (WhatsApp 2026-09-24
 * sur Tom Angeli) : la pastille rouge était lue comme un état alors
 * qu'elle était un bouton d'action. Ici on distingue clairement :
 *   - bouton "Rompre l'essai" (action → si pas encore rompu)
 *   - badge  "🚫 Rompu le 22/09 par Camille V." (état → non cliquable)
 *
 * Le badge affiche aussi la date effective en tooltip pour audit rapide.
 */
function TrialEndedBadge({ item }) {
    const at = item.trial_terminated_at;
    if (!at) return null;
    const day = fmtDay(at);
    const by = item.trial_terminated_by_name || "—";
    const effective = item.trial_terminated_effective_date;
    const effectiveDay = effective ? fmtDay(effective) : null;
    const title = effectiveDay
        ? `Rupture actée le ${day} par ${by} · fin de contrat au ${effectiveDay}`
        : `Rupture actée le ${day} par ${by}`;
    return (
        <div
            data-testid={`trial-ended-badge-${item.candidate_id}`}
            title={title}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-red-300 bg-red-100 text-red-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest cursor-help"
        >
            <Ban className="w-3 h-3" />
            <span className="truncate">
                Rompu {day} · {by}
            </span>
        </div>
    );
}



function ResendFicheAction({ candidateId, sentAt, openedAt, reminderSentAt, onDone }) {
    const [busy, setBusy] = useState(false);
    const daysSince = useMemo(() => {
        const anchor = reminderSentAt || sentAt;
        if (!anchor) return null;
        const then = new Date(anchor).getTime();
        if (Number.isNaN(then)) return null;
        return Math.max(0, Math.floor((Date.now() - then) / (24 * 3600 * 1000)));
    }, [sentAt, reminderSentAt]);

    // Urgency signal — orange after 3 days without opening
    const stale = daysSince !== null && daysSince >= 3 && !openedAt;

    async function handleClick(e) {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await onDone?.(candidateId);
        } finally {
            // Small extra debounce window so rapid double-clicks can't
            // hit the backend twice even before the 60s dedupe kicks in.
            setTimeout(() => setBusy(false), 800);
        }
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={busy}
            data-testid={`embauche-card-resend-${candidateId}`}
            title={
                openedAt
                    ? "Le candidat a ouvert la fiche mais ne l'a pas complétée — relance douce"
                    : `Fiche envoyée${daysSince !== null ? ` il y a ${daysSince} jour${daysSince > 1 ? "s" : ""}` : ""} sans ouverture — renvoyer un nouveau lien`
            }
            className={`mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition border-2 disabled:opacity-60 disabled:cursor-wait ${
                stale
                    ? "border-shariti-orange bg-shariti-orange-soft text-shariti-orange hover:bg-shariti-orange hover:text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-shariti-orange hover:text-shariti-orange"
            }`}
        >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Envoi…" : (stale ? "Relancer" : "Renvoyer la fiche")}
        </button>
    );
}

function StageDetail({ stage, item }) {
    if (stage === "to_trigger") return <span>Prêt à envoyer</span>;
    if (stage === "form_sent") return <span>Envoyée le {fmtDay(item.onboarding?.sent_at)}</span>;
    if (stage === "form_received") return <span>Reçue le {fmtDay(item.onboarding?.submitted_at || item.onboarding?.updated_at)}</span>;
    if (stage === "contract_ready") {
        const cs = item.contract?.status;
        if (cs === "sent" || cs === "opened") return <span>Envoyé le {fmtDay(item.contract?.sent_at)}</span>;
        return <span>Contrat préparé</span>;
    }
    if (stage === "signed") return <span>Signé le {fmtDay(item.contract?.signed_at)}</span>;
    return null;
}

// ---------- Drawer ----------------------------------------------------------

// iter110n · CEO 2026-09-27 · Bloc contextuel dans le drawer candidat :
// 3 boutons pour créer un bon de remise (nouveau/restitution/régularisation).
function EmbaucheDeliveryButtons({ item, onRefresh }) {
    const [openType, setOpenType] = useState(null);
    return (
        <>
            <div className="rounded-xl border border-shariti-border p-3 space-y-2">
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
                    <span>📦</span> Bons de remise matériel
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <button
                        onClick={() => setOpenType("initial")}
                        data-testid="embauche-drawer-delivery-note-new"
                        className="text-xs font-semibold px-2 py-2 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-800 transition"
                    >
                        Nouveau bon
                    </button>
                    <button
                        onClick={() => setOpenType("return")}
                        data-testid="embauche-drawer-delivery-note-return"
                        className="text-xs font-semibold px-2 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-800 transition"
                    >
                        Restitution
                    </button>
                    <button
                        onClick={() => setOpenType("regularization")}
                        data-testid="embauche-drawer-delivery-note-regul"
                        className="text-xs font-semibold px-2 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 transition"
                    >
                        Régularisation
                    </button>
                </div>
            </div>
            <DeliveryNoteModal
                open={!!openType}
                onOpenChange={(v) => !v && setOpenType(null)}
                subjectType="candidate"
                subjectId={item.candidate_id}
                subjectName={`${item.first_name} ${item.last_name}`.trim()}
                defaultType={openType || "initial"}
                onCompleted={() => {
                    // iter110n · Ne PAS fermer le modal ici — l'étape 4
                    // (SHA-256 + téléchargement PDF) doit rester visible.
                    // L'utilisateur ferme lui-même via le bouton « Fermer ».
                    onRefresh?.();
                }}
            />
        </>
    );
}


function CandidateDrawer({ item, onClose, onRefresh }) {
    const navigate = useNavigate();
    const name = `${item.first_name} ${item.last_name}`.trim();

    // Escape closes the drawer
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-40 flex" data-testid="embauche-drawer">
            <button
                type="button"
                aria-label="Fermer"
                onClick={onClose}
                className="flex-1 bg-black/40 backdrop-blur-sm"
                data-testid="embauche-drawer-backdrop"
            />
            <aside className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
                <div className="sticky top-0 z-10 bg-white border-b border-shariti-border px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center text-white font-black flex-shrink-0">
                            {initials(`${item.first_name} ${item.last_name}`)}
                        </div>
                        <div className="min-w-0">
                            <div className="font-black text-shariti-black break-words">{name}</div>
                            <div className="text-xs text-slate-500 break-words">{item.email}</div>
                            {/* Matricule badge — same visual signature as
                                Kanban / profile / notifications. Copy-to-
                                clipboard for one-click paste into Evergiving. */}
                            {item.code_rd && (
                                <div className="mt-1.5">
                                    <CodeRdBadge
                                        code={item.code_rd}
                                        candidateId={item.candidate_id}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="embauche-drawer-close"
                        className="p-1.5 rounded-full hover:bg-slate-100 transition"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Quick action to open full profile */}
                    <button
                        type="button"
                        onClick={() => navigate(`/candidats/${item.candidate_id}`)}
                        data-testid="embauche-drawer-open-profile"
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-shariti-border hover:border-shariti-orange hover:bg-shariti-orange-soft/20 transition text-sm font-bold text-shariti-black"
                    >
                        <span>Ouvrir le profil complet</span>
                        <ArrowRight className="w-4 h-4" />
                    </button>

                    {/* iter110n · CEO 2026-09-27 · Bons de remise matériel.
                        2 boutons : nouveau bon (initial/complementary/return)
                        + régularisation (salarié déjà en poste). Ouvre
                        le modal DeliveryNoteModal complet. */}
                    <EmbaucheDeliveryButtons item={item} onRefresh={onRefresh} />

                    <TimelineSection item={item} onRefresh={onRefresh} />
                    <OnboardingSection item={item} />
                    <ContractSection item={item} onRefresh={onRefresh} />
                    <AcademySection item={item} />
                    <EmailOpensSection item={item} />
                </div>
            </aside>
        </div>
    );
}

// ---------- Timeline --------------------------------------------------------

/**
 * Preview modal used by TimelineSection to display, without leaving the
 * Kanban drawer, either:
 *   - the exact email that was sent (kind === "email:*") — with delivery
 *     status pill so admins immediately see if it arrived, was redirected
 *     in preview mode, dropped by dedupe, or failed with an SMTP error ;
 *   - the onboarding form the candidate filled in (kind === "form_data")
 *     — every civil / bank / family / doc field, ready to be checked.
 *
 * Contract PDF previews bypass this modal — they open the PDF in a new
 * tab via the pre-existing `openContractPdf` helper.
 */
function TimelinePreviewModal({ candidateId, row, onClose, onResent }) {
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setState({ loading: true, error: null, data: null });
            try {
                if (row.previewKind?.startsWith("email:")) {
                    const emailKind = row.previewKind.split(":")[1];
                    const { data } = await api.get(
                        `/emails/candidate/${candidateId}`,
                        { params: { kind: emailKind } },
                    );
                    if (!cancelled) {
                        setState({ loading: false, error: null, data: { type: "email", rows: Array.isArray(data) ? data : [] } });
                    }
                } else if (row.previewKind === "form_data") {
                    const { data } = await api.get(`/onboarding/candidates/${candidateId}`);
                    if (!cancelled) {
                        setState({ loading: false, error: null, data: { type: "form", form: data } });
                    }
                }
            } catch (e) {
                if (!cancelled) {
                    const msg = e?.response?.data?.detail || "Impossible de charger l'aperçu";
                    setState({ loading: false, error: msg, data: null });
                }
            }
        }
        load();
        return () => { cancelled = true; };
    }, [candidateId, row.previewKind, reloadKey]);

    // Called after a successful resend so the modal refetches the latest
    // log row (which will show the new send at the top of the history).
    const handleResent = () => {
        setReloadKey((k) => k + 1);
        onResent?.();
    };

    return (
        <div
            className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="embauche-timeline-preview-modal"
        >
            <div
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                            Aperçu — {row.label}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {row.date ? fmtDate(row.date) : "en attente"}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="embauche-timeline-preview-close"
                        className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2"
                        aria-label="Fermer"
                    >
                        ×
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
                    {state.loading && (
                        <div className="flex items-center justify-center py-12 text-sm text-slate-500 gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
                        </div>
                    )}
                    {state.error && (
                        <div className="text-sm text-red-800 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                            {state.error}
                        </div>
                    )}
                    {!state.loading && !state.error && state.data?.type === "email" && (
                        <EmailPreviewBody
                            rows={state.data.rows}
                            candidateId={candidateId}
                            kind={row.previewKind?.split(":")[1]}
                            onResent={handleResent}
                        />
                    )}
                    {!state.loading && !state.error && state.data?.type === "form" && (
                        <FilledFormBody form={state.data.form} candidateId={candidateId} />
                    )}
                </div>
            </div>
        </div>
    );
}

const _PROVIDER_PILL = {
    "resend":          { label: "Envoyé (Resend)", tone: "emerald" },
    "infomaniak-smtp": { label: "Envoyé (SMTP)",    tone: "emerald" },
    "dry-run":         { label: "Dry-run",          tone: "slate" },
    "dedupe":          { label: "Dédupliqué",       tone: "amber" },
    "mock":            { label: "Mock",             tone: "slate" },
    "smtp-error":      { label: "Échec envoi",      tone: "red" },
};

const _TONE_CLASS = {
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    amber:   "bg-amber-100 text-amber-900 border-amber-200",
    red:     "bg-red-100 text-red-900 border-red-200",
};

function EmailPreviewBody({ rows, candidateId, kind, onResent }) {
    const [resending, setResending] = useState(false);
    if (!rows || rows.length === 0) {
        return (
            <div className="text-sm text-slate-500 italic text-center py-8">
                Aucun email trouvé pour ce candidat à ce stade.
            </div>
        );
    }
    // Newest first — show most recent send at the top with delivery status pill.
    const latest = rows[0];
    const info = _PROVIDER_PILL[latest.provider] || { label: latest.provider, tone: "slate" };
    // Whether the "Renvoyer" shortcut should appear.
    //   - Only for kinds we can actually re-fire: onboarding_invite, contract_sent
    //   - Only when the last attempt was a soft/hard failure (SMTP error, dedupe
    //     drop, mock stub) — for successful `infomaniak-smtp` sends we hide it
    //     to prevent accidental double-send.
    const resendable = ["onboarding_invite", "contract_sent"].includes(kind || "");
    const shouldOfferResend = resendable && ["smtp-error", "dedupe", "mock", "bounced"].includes(latest.provider);

    const handleResend = async () => {
        if (!candidateId || !kind) return;
        setResending(true);
        try {
            const { data } = await api.post(`/emails/resend`, null, {
                params: { candidate_id: candidateId, kind },
            });
            const ok = data?.ok !== false && !data?.email_error;
            toast[ok ? "success" : "error"](
                ok ? "Email renvoyé au candidat" : (data?.email_error || "Échec du renvoi"),
            );
            onResent?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossible de renvoyer l'email");
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Status header */}
            <div className="rounded-2xl bg-white border-2 border-slate-200 p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${_TONE_CLASS[info.tone]}`} data-testid="timeline-preview-status">
                        {info.label}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {latest.environment === "prod" ? "Production" : "Preview"}
                    </span>
                    {latest.redirected && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                            Redirigé (safeguard preview)
                        </span>
                    )}
                    {/* Open tracking — the strongest "the candidate actually read it" signal we can offer. */}
                    {latest.opened_at ? (
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-sky-100 text-sky-900 border border-sky-200"
                            data-testid="timeline-preview-open-status"
                            title={`Ouvert le ${(latest.opened_at || "").slice(0, 19).replace("T", " à ")} · ${latest.open_count || 1} ouverture(s)`}
                        >
                            <Eye className="w-3 h-3" />
                            Ouvert{latest.open_count > 1 ? ` ×${latest.open_count}` : ""}
                        </span>
                    ) : ["resend", "infomaniak-smtp"].includes(latest.provider) ? (
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200"
                            data-testid="timeline-preview-open-status"
                            title="Aucune ouverture détectée pour l'instant"
                        >
                            <Mail className="w-3 h-3" />
                            Non ouvert
                        </span>
                    ) : null}
                </div>
                <div className="text-sm font-black text-shariti-black break-words">
                    {latest.subject}
                </div>
                <div className="text-xs text-slate-500 font-mono break-all">
                    {latest.redirected && latest.original_to !== latest.to ? (
                        <>
                            <span className="line-through text-slate-400">{latest.original_to}</span>
                            <ArrowRight className="inline h-3 w-3 mx-1 -mt-0.5" />
                            <strong>{latest.to}</strong>
                        </>
                    ) : (
                        <span>Destinataire · <strong>{latest.to}</strong></span>
                    )}
                    <div className="mt-1">Envoyé le {(latest.sent_at || "").slice(0, 19).replace("T", " à ")}</div>
                </div>
                {latest.error && (
                    <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-2 font-mono">
                        <strong>Erreur SMTP :</strong> {latest.error}
                    </div>
                )}
                {shouldOfferResend && (
                    <div className="pt-1">
                        <button
                            type="button"
                            disabled={resending}
                            onClick={handleResend}
                            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black uppercase tracking-wider text-[11px] h-8 px-4 transition"
                            data-testid="timeline-preview-resend-btn"
                        >
                            {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            {resending ? "Envoi…" : "Renvoyer ce mail"}
                        </button>
                    </div>
                )}
            </div>

            {/* HTML preview — the outer modal already scrolls vertically
                (max-h-[90vh]). We remove overflow-hidden here so long
                emails aren't visually truncated, and add overflow-x-auto
                because the Shariti email shell is fixed at 600px width
                (would otherwise get cropped on a narrow drawer). */}
            {latest.html_preview ? (
                <div
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 overflow-x-auto"
                    data-testid="timeline-preview-html"
                    dangerouslySetInnerHTML={{ __html: latest.html_preview }}
                />
            ) : (
                <div className="text-xs text-slate-400 italic">Contenu HTML indisponible pour cet envoi.</div>
            )}

            {/* If there are multiple sends (relance / dedupe), summarise them */}
            {rows.length > 1 && (
                <div className="text-xs text-slate-500">
                    <div className="font-black uppercase tracking-widest text-slate-400 mb-2">Historique · {rows.length} envois</div>
                    <ul className="space-y-1">
                        {rows.map((r) => (
                            <li key={r.id} className="flex items-center gap-2 py-1 border-t border-slate-100">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${_TONE_CLASS[(_PROVIDER_PILL[r.provider] || { tone: "slate" }).tone]}`}>
                                    {(_PROVIDER_PILL[r.provider] || { label: r.provider }).label}
                                </span>
                                <span className="text-slate-600">{(r.sent_at || "").slice(0, 19).replace("T", " ")}</span>
                                {r.redirected && <span className="text-amber-700 text-[10px]">redirigé</span>}
                                {r.opened_at && <span className="text-sky-700 text-[10px]">ouvert</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// Field labels shown in the "Fiche remplie par le candidat" modal. Keys
// MUST match the OnboardingForm.jsx canonical schema — otherwise fields
// silently render as "—" while the data IS in the DB (CEO reported bug
// 2026-07-27: SSN was missing from the preview because we were reading
// `nir` while the form persists `social_security_number`).
const _FORM_SECTIONS = [
    {
        title: "État civil",
        fields: [
            ["civilite", "Civilité"],
            ["last_name", "Nom"],
            ["first_name", "Prénom"],
            ["maiden_name", "Nom de jeune fille"],
            ["birth_date", "Date de naissance"],
            ["birth_city", "Ville de naissance"],
            ["birth_dept", "Département de naissance"],
            ["birth_country", "Pays de naissance"],
            ["nationality", "Nationalité"],
            ["social_security_number", "N° sécurité sociale"],
        ],
    },
    {
        title: "Titre de séjour / travail (étrangers)",
        // Only rendered when at least one field is filled — otherwise
        // we hide the whole section so French candidates don't see a
        // pile of empty rows.
        conditional: (data) => !!(
            data.foreign_status || data.permit_number || data.permit_type ||
            data.permit_expiry || data.permit_issue_place
        ),
        fields: [
            ["foreign_status", "Statut"],
            ["permit_number", "N° de titre"],
            ["permit_type", "Type"],
            ["permit_expiry", "Date d'expiration"],
            ["permit_issue_place", "Lieu de délivrance"],
        ],
    },
    {
        title: "Coordonnées",
        fields: [
            ["email", "Email"],
            ["phone_mobile", "Téléphone mobile"],
            ["phone_landline", "Téléphone fixe"],
            ["address_line1", "Adresse"],
            ["address_postal_code", "Code postal"],
            ["address_city", "Ville"],
        ],
    },
    {
        title: "Personne à prévenir (accident)",
        conditional: (data) => !!(
            data.emergency_name || data.emergency_phone_mobile || data.emergency_phone_landline
        ),
        fields: [
            ["emergency_name", "Nom"],
            ["emergency_phone_mobile", "Mobile"],
            ["emergency_phone_landline", "Fixe"],
        ],
    },
    {
        title: "Situation familiale",
        fields: [
            ["marital_status", "Situation"],
            ["spouse_name", "Conjoint"],
            ["nb_adults_dependent", "Adultes à charge"],
            ["nb_children_dependent", "Enfants à charge"],
        ],
    },
    {
        title: "Coordonnées bancaires",
        fields: [
            ["iban", "IBAN"],
            ["bic", "BIC"],
        ],
    },
];

function _fmtFormValue(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Oui" : "Non";
    return String(v);
}

// iter101 · CEO 2026-09-06 : le CEO avait la photo du titre de séjour de
// Comlan uploadée par le candidat mais AUCUN moyen de la voir depuis
// l'aperçu. Le bouton est authentifié (Bearer) donc on fetch la ressource
// en blob et on l'ouvre dans un nouvel onglet.
async function _openDocInNewTab(candidateId, docKind) {
    try {
        const token = tokenStore.get();
        const res = await fetch(`${API_BASE}/onboarding/candidates/${candidateId}/doc/${docKind}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const detail = res.status === 404
                ? "Document introuvable"
                : `Erreur ${res.status}`;
            toast.error(detail);
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank", "noopener,noreferrer");
        // Cleanup delayed pour laisser le navigateur charger l'onglet
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        if (!win) toast.warning("Autorisez les pop-ups pour voir le document");
    } catch (e) {
        toast.error("Impossible d'ouvrir le document");
    }
}

function FilledFormBody({ form, candidateId }) {
    if (!form || !form.data) {
        return (
            <div className="text-sm text-slate-500 italic text-center py-8">
                Le candidat n&apos;a pas encore rempli sa fiche.
            </div>
        );
    }
    const data = form.data || {};
    // iter101 · `documents` du back est un DICT {kind: {filename, storage_path, ...}}
    // pas une liste. On le normalise en liste avec le kind comme clé.
    const rawDocs = form.documents || {};
    const docs = Array.isArray(rawDocs)
        ? rawDocs
        : Object.entries(rawDocs)
              .filter(([, v]) => v)
              .map(([kind, v]) => ({ kind, ...v }));
    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-white border-2 border-slate-200 p-4 flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 font-black uppercase tracking-wider text-[10px]">
                    {form.status || "?"}
                </span>
                {form.submitted_at && (
                    <span className="text-slate-500">Soumise le {fmtDate(form.submitted_at)}</span>
                )}
                {form.progress != null && (
                    <span className="ml-auto text-[10px] font-black text-slate-400">
                        Progression {Math.round((form.progress || 0) * 100)}%
                    </span>
                )}
            </div>

            {_FORM_SECTIONS.map((section) => {
                // Some sections (foreign / emergency contact) render only
                // when the candidate actually filled at least one field —
                // avoids a wall of "—" for French recruits.
                if (section.conditional && !section.conditional(data)) return null;
                return (
                    <div key={section.title} className="rounded-2xl bg-white border border-slate-200 p-4">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3">
                            {section.title}
                        </div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            {section.fields.map(([key, label]) => (
                                <div key={key} className="flex flex-col">
                                    <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</dt>
                                    <dd className="text-slate-800 break-words">{_fmtFormValue(data[key])}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                );
            })}

            {Array.isArray(data.dependents) && data.dependents.length > 0 && (
                <div className="rounded-2xl bg-white border border-slate-200 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3">
                        Personnes à charge ({data.dependents.length})
                    </div>
                    <ul className="space-y-2 text-sm">
                        {data.dependents.map((d, i) => (
                            <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                                <span>{d.first_name} {d.last_name}</span>
                                <span className="text-xs text-slate-500 font-mono">{d.birth_date || "—"}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {docs.length > 0 && (
                <div className="rounded-2xl bg-white border border-slate-200 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3">
                        Documents joints ({docs.length})
                    </div>
                    <ul className="space-y-1 text-sm">
                        {docs.map((d) => (
                            <li key={d.kind} className="flex items-center justify-between gap-2 py-1">
                                <span className="capitalize truncate flex-1">{d.kind?.replace(/_/g, " ")}</span>
                                <span className="text-xs text-slate-500 font-mono truncate max-w-[160px]">{d.filename || "—"}</span>
                                {/* iter101 · Ouvrir le document dans un nouvel onglet (authentifié via blob) */}
                                {candidateId && (
                                    <button
                                        type="button"
                                        onClick={() => _openDocInNewTab(candidateId, d.kind)}
                                        data-testid={`preview-open-doc-${d.kind}`}
                                        title="Ouvrir le document dans un nouvel onglet"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-shariti-orange text-white text-[10px] font-black uppercase tracking-wider hover:bg-shariti-orange-hover transition shrink-0"
                                    >
                                        <Eye className="h-3 w-3" />
                                        Voir
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


function TimelineSection({ item, onRefresh }) {
    const hasAcademyClick = !!item.academy?.clicked_at;
    const contractId = item.contract?.id;
    const [preview, setPreview] = useState(null);
    const [sendingAcademy, setSendingAcademy] = useState(false);
    // Business rule: once the candidate has clicked their Academy invitation,
    // no J+3 reminder will EVER fire (the scheduler filters on `clicked_at: None`).
    // Showing the "en attente" line in that case is a lie — hide it entirely.
    const rows = [
        { key: "form_sent", label: "Fiche envoyée", date: item.onboarding?.sent_at, icon: Send, previewKind: "email:onboarding_invite" },
        { key: "form_submitted", label: "Fiche remplie par le candidat", date: item.onboarding?.submitted_at, icon: ClipboardCheck, previewKind: "form_data" },
        { key: "contract_prepared", label: "Contrat préparé", date: item.contract?.created_at, icon: FileText, previewKind: contractId ? "contract_pdf" : null },
        { key: "contract_sent", label: "Lien de signature envoyé", date: item.contract?.sent_at, icon: FileSignature, previewKind: "email:contract_sent" },
        { key: "contract_signed", label: "Contrat signé", date: item.contract?.signed_at, icon: CheckCircle2, previewKind: contractId ? "signed_pdf" : null },
        { key: "academy_sent", label: "Email Academy envoyé", date: item.academy?.invitation_sent_at, icon: GraduationCap, previewKind: "email:academy_welcome" },
        { key: "academy_clicked", label: "Academy cliqué par le candidat", date: item.academy?.clicked_at, icon: MousePointerClick, previewKind: null },
        // Reminder line is hidden once the candidate has already clicked — no
        // need to promise a J+3 nudge that will never fire.
        !hasAcademyClick && { key: "academy_reminder", label: "Rappel Academy J+3 envoyé", date: item.academy?.reminder_sent_at, icon: Mail, previewKind: "email:academy_reminder" },
    ].filter(Boolean);

    const handlePreview = (row) => {
        // Contracts open the PDF directly; email + form_data open the modal.
        if (row.previewKind === "contract_pdf") {
            openContractPdf(contractId, "preview");
            return;
        }
        if (row.previewKind === "signed_pdf") {
            openContractPdf(contractId, "signed");
            return;
        }
        setPreview(row);
    };

    // Manual "Envoyer maintenant" button on the pending Academy row — used
    // when a candidate signed OUTSIDE the PREREC flow (paper contract) and
    // still needs Academy access.
    const handleSendAcademy = async () => {
        if (sendingAcademy) return;
        setSendingAcademy(true);
        try {
            const { data } = await api.post(`/candidates/${item.candidate_id}/send-academy-invite`);
            const ok = data?.ok !== false && !data?.email_error;
            toast[ok ? "success" : "error"](
                ok ? "Invitation Academy envoyée." : (data?.email_error || "Échec de l'envoi Academy"),
            );
            onRefresh?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible d'envoyer l'invitation Academy");
        } finally {
            setSendingAcademy(false);
        }
    };

    // Manual "Relancer maintenant" — bypasses the 60min scheduler tick +
    // the 72h delay filter for THIS candidate. Reuses the same email
    // template + tracking URL as the automatic tick so the "Rappel
    // Academy" timeline row picks it up identically. Guarded server-side
    // against already-clicked / already-reminded invitations (409).
    const [sendingReminder, setSendingReminder] = useState(false);
    const handleSendReminderNow = async () => {
        if (sendingReminder) return;
        setSendingReminder(true);
        try {
            await api.post(`/candidates/${item.candidate_id}/send-academy-reminder`);
            toast.success("Relance Academy envoyée.");
            onRefresh?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible d'envoyer la relance");
        } finally {
            setSendingReminder(false);
        }
    };

    return (
        <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange mb-3">
                Chronologie
            </div>
            <ol className="space-y-3">
                {rows.map((r) => {
                    const done = !!r.date;
                    const Icon = r.icon;
                    // Show preview button only when the event happened AND we
                    // know how to preview it. Academy click has no dedicated
                    // preview (it's just a status).
                    const canPreview = done && r.previewKind;
                    // Manual send affordance — appears on the `academy_sent`
                    // row whatever its state:
                    //   - `!done`  → primary "Envoyer maintenant" (candidat signé hors PREREC)
                    //   - `done`   → secondary "Renvoyer" (candidat n'a pas reçu / a perdu l'email)
                    // Both hit the same endpoint (POST /candidates/{id}/send-academy-invite)
                    // which creates a fresh `academy_invitations` row + tracked link.
                    const canManualSendAcademy = !done && r.key === "academy_sent";
                    const canResendAcademy = done && r.key === "academy_sent";
                    // "Relancer maintenant" — visible on the pending
                    // academy_reminder row IF the invitation was actually
                    // sent (otherwise there's nothing to remind about) AND
                    // the candidate hasn't clicked yet (server enforces
                    // the same rules with a 409).
                    const canSendReminderNow = (
                        !done
                        && r.key === "academy_reminder"
                        && !!item.academy?.invitation_sent_at
                        && !item.academy?.clicked_at
                    );
                    return (
                        <li key={r.key} className="flex items-start gap-3" data-testid={`embauche-timeline-${r.key}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                                <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className={`text-sm ${done ? "text-shariti-black font-semibold" : "text-slate-400"}`}>
                                    {r.label}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                    {done ? fmtDate(r.date) : "en attente"}
                                </div>
                            </div>
                            {canPreview && (
                                <button
                                    type="button"
                                    onClick={() => handlePreview(r)}
                                    data-testid={`embauche-timeline-preview-${r.key}`}
                                    title={`Aperçu — ${r.label}`}
                                    className="mt-0.5 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 hover:border-shariti-orange hover:bg-shariti-orange-soft/40 hover:text-shariti-orange text-slate-500 transition text-[11px] font-bold"
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                    Aperçu
                                </button>
                            )}
                            {canManualSendAcademy && (
                                <button
                                    type="button"
                                    onClick={handleSendAcademy}
                                    disabled={sendingAcademy}
                                    data-testid="embauche-timeline-send-academy"
                                    title="Envoyer l'email Academy manuellement (signé hors PREREC)"
                                    className="mt-0.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-60 text-white transition text-[11px] font-black uppercase tracking-wider"
                                >
                                    {sendingAcademy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    {sendingAcademy ? "Envoi…" : "Envoyer maintenant"}
                                </button>
                            )}
                            {canResendAcademy && (
                                <button
                                    type="button"
                                    onClick={handleSendAcademy}
                                    disabled={sendingAcademy}
                                    data-testid="embauche-timeline-resend-academy"
                                    title="Renvoyer l'invitation Academy (le candidat n'a pas reçu / a perdu l'email)"
                                    className="mt-0.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-shariti-orange text-shariti-orange hover:bg-shariti-orange-soft/40 disabled:opacity-60 transition text-[11px] font-black uppercase tracking-wider"
                                >
                                    {sendingAcademy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                    {sendingAcademy ? "Envoi…" : "Renvoyer"}
                                </button>
                            )}
                            {canSendReminderNow && (
                                <button
                                    type="button"
                                    onClick={handleSendReminderNow}
                                    disabled={sendingReminder}
                                    data-testid="embauche-timeline-send-reminder-now"
                                    title="Envoyer la relance Academy maintenant (ignore le délai J+3)"
                                    className="mt-0.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white transition text-[11px] font-black uppercase tracking-wider"
                                >
                                    {sendingReminder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                    {sendingReminder ? "Envoi…" : "Relancer maintenant"}
                                </button>
                            )}
                        </li>
                    );
                })}
            </ol>
            {preview && (
                <TimelinePreviewModal
                    candidateId={item.candidate_id}
                    row={preview}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
}

// ---------- Onboarding section ---------------------------------------------

function OnboardingSection({ item }) {
    const ob = item.onboarding;
    if (!ob) return null;
    const statusLabel = {
        sent: "Envoyée", opened: "Ouverte", in_progress: "En cours",
        submitted: "Soumise", approved: "Approuvée", needs_fix: "Correction demandée",
    }[ob.status] || ob.status;
    // iter101 · CEO 2026-09-06 : quand la fiche est SOUMISE, la RH doit
    // pouvoir aller directement au dossier d'embauche pour l'approuver /
    // demander une correction / préparer le contrat, SANS avoir à
    // cliquer sur "Ouvrir le profil complet" et scroller. On ajoute des
    // CTAs directs qui pointent vers la CandidateProfile avec un anchor
    // `#onboarding` — la page scrolle automatiquement au bloc concerné.
    const needsAdminAction = ob.status === "submitted";
    const readyForContract = ob.status === "approved" && !item.contract;

    return (
        <div className={`rounded-xl border p-4 ${needsAdminAction ? "border-amber-300 bg-amber-50/50" : readyForContract ? "border-emerald-300 bg-emerald-50/50" : "border-shariti-border bg-slate-50/50"}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                    Fiche d&apos;embauche
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${needsAdminAction ? "bg-amber-100 text-amber-800" : readyForContract ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                    {statusLabel}
                </span>
            </div>
            <div className="text-sm text-slate-600 mb-3">
                {ob.has_data ? (
                    needsAdminAction ? "La RH doit valider ou demander une correction avant de préparer le contrat."
                    : readyForContract ? "Fiche approuvée — vous pouvez préparer le contrat."
                    : "Les infos et documents saisis par le candidat sont visibles sur le profil complet."
                ) : (
                    "Le candidat n'a pas encore rempli sa fiche."
                )}
            </div>
            {ob.has_data && (
                <div className="flex flex-wrap gap-2">
                    <Link
                        to={`/candidats/${item.candidate_id}#onboarding`}
                        data-testid="drawer-open-fiche-cta"
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black transition ${
                            needsAdminAction
                                ? "bg-shariti-orange text-white hover:bg-shariti-orange-hover"
                                : "border border-shariti-border bg-white hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        <ClipboardCheck className="h-3.5 w-3.5" />
                        {needsAdminAction ? "Valider / demander correction" : "Ouvrir la fiche"}
                    </Link>
                    {readyForContract && (
                        <Link
                            to={`/candidats/${item.candidate_id}#contract`}
                            data-testid="drawer-prepare-contract-cta"
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black bg-emerald-600 text-white hover:bg-emerald-700 transition"
                        >
                            <FileText className="h-3.5 w-3.5" />
                            Préparer le contrat
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Contract section ------------------------------------------------

async function openContractPdf(contractId, kind) {
    try {
        const path = kind === "signed"
            ? `/contracts/${contractId}/signed.pdf`
            : `/contracts/${contractId}/preview.pdf`;
        const res = await api.get(path, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(res.data);
        window.open(blobUrl, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
        toast.error("Impossible d'ouvrir le PDF");
    }
}

function ContractSection({ item, onRefresh }) {
    const c = item.contract;
    if (!c) return null;
    const statusLabel = {
        draft: "Brouillon", sent: "Envoyé", opened: "Ouvert", signed: "Signé",
        archived: "Archivé", voided: "Annulé",
    }[c.status] || c.status;

    // iter101 · Le RenewContractDialog attend `contract.params.{start,end}_date`
    // alors que le pipeline hiring aplatit ces champs à la racine. On construit
    // un shim ici pour ne pas modifier le composant partagé.
    const contractForRenew = {
        id: c.id,
        params: {
            start_date: c.start_date,
            end_date: c.end_date,
        },
    };

    return (
        <div className="rounded-xl border border-shariti-border p-4 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                    Contrat
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${c.status === "signed" ? "bg-emerald-100 text-emerald-800" : "bg-purple-100 text-purple-800"}`}>
                    {statusLabel}
                </span>
            </div>
            <div className="text-sm text-slate-600 space-y-1">
                {c.start_date && c.end_date && (
                    <div>Du {fmtDay(c.start_date)} au {fmtDay(c.end_date)}</div>
                )}
                {c.contract_type && (
                    <div className="text-xs text-slate-500">
                        {c.contract_type === "partial" ? "Temps partiel" : "Temps plein"}
                    </div>
                )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
                <button
                    type="button"
                    onClick={() => openContractPdf(c.id, "preview")}
                    data-testid="embauche-drawer-preview-pdf"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-shariti-border bg-white hover:border-shariti-orange hover:text-shariti-orange transition text-xs font-bold"
                >
                    <Eye className="w-3.5 h-3.5" /> Aperçu PDF
                </button>
                {c.status === "signed" && (
                    <button
                        type="button"
                        onClick={() => openContractPdf(c.id, "signed")}
                        data-testid="embauche-drawer-signed-pdf"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition text-xs font-bold"
                    >
                        <Download className="w-3.5 h-3.5" /> PDF signé
                    </button>
                )}
                {/* iter101 · Renouveler un contrat signé. Le composant partagé
                    RenewContractDialog gère les nouvelles dates + appelle
                    /contracts/{id}/renew qui envoie automatiquement le lien
                    de signature au candidat. */}
                {c.status === "signed" && (
                    <RenewContractDialog
                        contract={contractForRenew}
                        onRenewed={() => onRefresh?.()}
                    />
                )}
            </div>
        </div>
    );
}

// ---------- Academy section -------------------------------------------------

function AcademySection({ item }) {
    const a = item.academy;
    if (!a) return null;
    return (
        <div className="rounded-xl border border-shariti-border p-4 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                    Shariti Academy
                </div>
                {a.clicked_at ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-wider">
                        Cliqué
                    </span>
                ) : a.reminder_sent_at ? (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider">
                        Relancé
                    </span>
                ) : (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider">
                        Envoyé
                    </span>
                )}
            </div>
            <div className="text-sm text-slate-600 space-y-1">
                <div>Email envoyé le <strong>{fmtDate(a.invitation_sent_at)}</strong></div>
                {a.clicked_at && <div>Cliqué le <strong>{fmtDate(a.clicked_at)}</strong></div>}
                {a.reminder_sent_at && <div>Rappel J+3 envoyé le <strong>{fmtDate(a.reminder_sent_at)}</strong></div>}
            </div>
        </div>
    );
}

// ---------- Email opens tracking (aggregated) ------------------------------
//
// Renders a compact "N envoyés · N ouverts" strip + an alert row when the
// candidate has ignored their mails for ≥ 24h. Feeds off the aggregated
// `emails_summary` computed by the /hiring/pipeline endpoint — zero extra
// API call per candidate. CEO reported the case on 2026-07-27: they need
// to spot upstream blockers ("le candidat lit-il ses mails ?") at a
// glance, not by drilling into every profile.

function EmailOpensSection({ item }) {
    const s = item.emails_summary;
    if (!s || !s.sent_count) return null;
    const openRate = s.sent_count > 0 ? Math.round((s.opened_count / s.sent_count) * 100) : 0;
    // Colour codes:
    //   red   = alert (≥24h, none opened)
    //   amber = some but not all opened
    //   green = 100% open rate
    let tone = "text-emerald-800 bg-emerald-100";
    let toneIcon = "text-emerald-700";
    if (s.has_unread_alert) {
        tone = "text-rose-800 bg-rose-100";
        toneIcon = "text-rose-700";
    } else if (s.opened_count < s.sent_count) {
        tone = "text-amber-800 bg-amber-100";
        toneIcon = "text-amber-700";
    }
    return (
        <div
            className="rounded-xl border border-shariti-border p-4 bg-slate-50/50"
            data-testid="embauche-drawer-email-opens"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                    Suivi des ouvertures d&apos;email
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${tone}`}>
                    {openRate}% ouvert
                </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex flex-col rounded-lg bg-white border border-slate-200 px-3 py-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Envoyés</span>
                    <span className="text-lg font-black text-shariti-black">{s.sent_count}</span>
                </div>
                <div className="flex flex-col rounded-lg bg-white border border-slate-200 px-3 py-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ouverts</span>
                    <span className={`text-lg font-black ${toneIcon}`}>{s.opened_count}</span>
                </div>
            </div>
            {s.has_unread_alert && (
                <div
                    className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-800"
                    data-testid="embauche-drawer-unread-alert"
                >
                    <MailWarning className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        Aucun email lu depuis <strong>{Math.round(s.unread_hours)}h</strong> — le
                        candidat pourrait être injoignable par mail. Envisagez un appel.
                    </span>
                </div>
            )}
            {!s.has_unread_alert && s.last_opened_at && (
                <div className="mt-2 text-[11px] text-slate-500">
                    Dernière ouverture : <strong>{fmtDate(s.last_opened_at)}</strong>
                </div>
            )}
        </div>
    );
}

// ---------- Empty state -----------------------------------------------------

function EmptyPipeline() {
    return (
        <div className="max-w-md mx-auto text-center py-16">
            <div className="w-14 h-14 rounded-full bg-shariti-orange-soft/40 flex items-center justify-center mx-auto mb-4">
                <Circle className="w-7 h-7 text-shariti-orange" />
            </div>
            <h2 className="text-lg font-black text-shariti-black">
                Aucun candidat dans le pipeline pour le moment
            </h2>
            <p className="text-sm text-slate-500 mt-2">
                Les candidats apparaîtront ici dès qu&apos;une fiche d&apos;embauche ou un contrat aura été déclenché depuis leur profil.
            </p>
        </div>
    );
}

// ---------- Manual drag-and-drop stage override modal ----------------------
//
// Fires when an admin drops a card onto a different column. Shows a clear
// warning banner (this bypasses the automated flow) + requires a reason
// >= 5 chars — the reason is stored in `status_history[]` as an audit
// trail so any anomaly downstream (e.g. Payfit export shows a
// manual_override contract) can be traced back to a named admin + reason.

function ForceStageModal({ move, onClose, onConfirmed }) {
    const { item, from_stage, to_stage } = move;
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const fromMeta = STAGE_META[from_stage];
    const toMeta = STAGE_META[to_stage];
    const name = `${item.first_name} ${item.last_name}`.trim() || item.email || "—";

    // Signed is the most sensitive target — creates a placeholder contract
    // flagged manual_override=true. Show a stronger warning banner then.
    const isSigned = to_stage === "signed";
    const isDowngrade =
        from_stage === "signed" && to_stage !== "signed";

    const canConfirm = reason.trim().length >= 5 && !busy;

    async function confirm() {
        if (!canConfirm) return;
        setBusy(true);
        try {
            const { data } = await api.post(
                `/hiring/candidates/${item.candidate_id}/force-stage`,
                { target_stage: to_stage, reason: reason.trim() },
            );
            if (data?.no_op) {
                toast.info("Aucun changement — candidat déjà dans cette colonne.");
            } else {
                toast.success(
                    `${name} déplacé vers "${STAGE_META[data.resolved_stage || to_stage].label}"`,
                );
            }
            onConfirmed?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Le déplacement a échoué";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            data-testid="embauche-force-stage-modal"
            onClick={busy ? undefined : onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header — orange band because this is an override, not a
                    destructive action. Reserve red for irreversible ops. */}
                <div className="bg-shariti-orange text-white px-6 py-4 flex items-center gap-3">
                    <ShieldAlert className="w-6 h-6 flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-xs font-bold uppercase tracking-widest opacity-80">
                            Override manuel admin
                        </div>
                        <div className="font-black text-lg leading-tight truncate">
                            Déplacer {name}
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {/* From → To visualisation */}
                    <div className="flex items-center gap-3 text-sm">
                        <span
                            className={`px-3 py-1 rounded-full font-black text-xs uppercase tracking-wider ${fromMeta.badge}`}
                            data-testid="embauche-force-stage-from"
                        >
                            {fromMeta.label}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span
                            className={`px-3 py-1 rounded-full font-black text-xs uppercase tracking-wider ${toMeta.badge}`}
                            data-testid="embauche-force-stage-to"
                        >
                            {toMeta.label}
                        </span>
                    </div>

                    {/* Contextual warning per target stage */}
                    {isSigned && (
                        <div className="rounded-lg bg-amber-50 border-2 border-amber-200 p-3 text-xs text-amber-900 space-y-1">
                            <div className="font-black flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Marquer comme signé sans contrat PREREC
                            </div>
                            <p>
                                Un contrat placeholder sera créé (flag <code>manual_override</code>), suffisant
                                pour débloquer l&apos;envoi Academy manuel. Cela ne remplace pas un vrai
                                contrat signé dans l&apos;outil. Utile quand la signature a eu lieu hors
                                PREREC (papier, autre plateforme).
                            </p>
                        </div>
                    )}
                    {isDowngrade && (
                        <div className="rounded-lg bg-red-50 border-2 border-red-200 p-3 text-xs text-red-900 space-y-1">
                            <div className="font-black flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Rétrogradation depuis « Signé »
                            </div>
                            <p>
                                Le contrat courant sera <strong>annulé</strong> (statut voided) — le lien de
                                signature devient inactif immédiatement. À n&apos;utiliser que si le contrat
                                a été mis à Signé par erreur.
                            </p>
                        </div>
                    )}
                    {!isSigned && !isDowngrade && (
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 space-y-1">
                            <p>
                                Ce déplacement <strong>bypass le workflow automatique</strong>. Les artefacts
                                sous-jacents (fiche, contrat) seront ajustés en minimum pour refléter la
                                nouvelle colonne — flag <code>manual_override</code> apposé pour l&apos;audit.
                            </p>
                        </div>
                    )}

                    {/* Reason (mandatory audit trail) */}
                    <div>
                        <label
                            htmlFor="force-stage-reason"
                            className="block text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5"
                        >
                            Raison <span className="text-red-500">*</span>{" "}
                            <span className="text-slate-400 font-normal normal-case tracking-normal">
                                (≥ 5 caractères — trace d&apos;audit)
                            </span>
                        </label>
                        <textarea
                            id="force-stage-reason"
                            data-testid="embauche-force-stage-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder={
                                isSigned
                                    ? "Ex. Contrat signé hors PREREC le 12/07 — débloquer l'envoi Academy"
                                    : "Ex. Erreur de manipulation lors de la précédente étape"
                            }
                            className="w-full rounded-lg border-2 border-slate-200 focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/20 outline-none px-3 py-2 text-sm"
                        />
                        <div className="mt-1 text-[10px] text-slate-400">
                            {reason.trim().length}/5 caractères minimum
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        data-testid="embauche-force-stage-cancel"
                        className="px-4 py-2 rounded-full border-2 border-slate-200 text-slate-700 hover:border-slate-400 text-xs font-black uppercase tracking-widest transition disabled:opacity-60"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={!canConfirm}
                        data-testid="embauche-force-stage-confirm"
                        className="px-4 py-2 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] text-xs font-black uppercase tracking-widest transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                        Confirmer le déplacement
                    </button>
                </div>
            </div>
        </div>
    );
}

